/** Decimal `match.format` code for Test cricket (multi-day). */
export const CRICKET_TEST_FORMAT_CODE = 999;

/** Decimal `match.format` code for The Hundred (100 balls). */
export const CRICKET_HUNDRED_FORMAT_CODE = 100;

/** Scheduled five-ball sets per innings for The Hundred. */
export const CRICKET_HUNDRED_SCHEDULED_OVERS = 20;

/** Keep aligned with backend `utils/cricketTestMatch.js` (Youth Tests when format is missing). */
const TEST_FORMAT_TEXT_RE =
  /\b(?:\d+(?:st|nd|rd|th)\s+tests?|youth\s+tests?|tests?\s+match(?:es)?|tests?\s+series|first[-\s]?class|county\s+championship|world\s+tests?\s+championship)\b|\btests?\b/i;

/**
 * True when a raw Decimal `format` value denotes Test cricket (999 or descriptive text).
 * @param {unknown} raw
 * @returns {boolean}
 */
export function isCricketTestFormatRaw(raw) {
  if (raw == null || raw === "") return false;
  const formatStr = String(raw).trim();
  const formatInt = parseInt(formatStr, 10);
  if (formatInt === CRICKET_TEST_FORMAT_CODE) return true;
  const lower = formatStr.toLowerCase();
  if (lower.includes("test") || lower.includes("first class") || lower.includes("first-class")) {
    return true;
  }
  return TEST_FORMAT_TEXT_RE.test(lower);
}

/**
 * Infer Test from competition / fixture title (e.g. "Youth Tests", "1st Test", WTC).
 * @param {unknown} competition
 * @param {unknown} [matchName]
 * @returns {boolean}
 */
export function isCricketTestMatchFromText(competition, matchName) {
  const blob = `${String(competition ?? "")} ${String(matchName ?? "")}`.trim();
  if (!blob) return false;
  return TEST_FORMAT_TEXT_RE.test(blob);
}

/**
 * Parse a format field to a numeric code: 999 for Test, 100 for The Hundred, 1–50 for limited overs, null if unknown.
 * @param {unknown} raw
 * @returns {number|null}
 */
export function normalizeCricketFormatCode(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    if (raw === CRICKET_TEST_FORMAT_CODE) return CRICKET_TEST_FORMAT_CODE;
    if (raw === CRICKET_HUNDRED_FORMAT_CODE) return CRICKET_HUNDRED_FORMAT_CODE;
    if (raw > 0 && raw <= 50) return raw;
    return null;
  }
  const s = String(raw).trim();
  const n = parseInt(s, 10);
  if (!Number.isNaN(n)) {
    if (n === CRICKET_TEST_FORMAT_CODE) return CRICKET_TEST_FORMAT_CODE;
    if (n === CRICKET_HUNDRED_FORMAT_CODE) return CRICKET_HUNDRED_FORMAT_CODE;
    if (n > 0 && n <= 50) return n;
  }
  if (isCricketTestFormatRaw(raw)) return CRICKET_TEST_FORMAT_CODE;
  const lower = s.toLowerCase();
  if (lower === "t20" || lower === "twenty20" || lower === "20") return 20;
  if (lower === "t10" || lower === "10") return 10;
  if (lower === "odi" || lower === "50" || lower === "one-day" || lower === "list a") return 50;
  if (/\bthe\s*hundred\b/.test(lower)) return CRICKET_HUNDRED_FORMAT_CODE;
  return null;
}

/**
 * Scheduled overs / five-ball sets for ov display and settlement (format 100 → 20).
 * @param {unknown} formatCode
 * @returns {number|null}
 */
export function cricketFormatScheduledOvers(formatCode) {
  const n = Number(formatCode);
  if (!Number.isFinite(n)) return null;
  if (n === CRICKET_HUNDRED_FORMAT_CODE) return CRICKET_HUNDRED_SCHEDULED_OVERS;
  if (n > 0 && n <= 50) return n;
  return null;
}

/** Delta legacy: Test fixtures use max_overs === -1. */
export function isCricketTestMaxOvers(maxOvers) {
  if (maxOvers == null || maxOvers === "") return false;
  const n = Number(maxOvers);
  return n === -1;
}

/** True when any innings max-overs value signals Test (999 or -1). */
export function isCricketTestMOvers(mOvers) {
  if (!Array.isArray(mOvers) || mOvers.length === 0) return false;
  return mOvers.some((value) => {
    if (value == null || value === "") return false;
    const n = Number(value);
    return n === CRICKET_TEST_FORMAT_CODE || n === -1 || String(value).trim() === "999";
  });
}

export function findCricketThreeWayMatchWinnerMarket(markets) {
  return (markets || []).find((market) => String(market?.market_type_id) === "15") ?? null;
}

/** Decimal 3-way Match Winner draw runner on Test matches. */
export function isCricketDrawSelectionName(name) {
  return String(name ?? "").trim().toUpperCase() === "DRAW";
}

/** Decimal 3-way Match Winner tie runner on limited-overs matches. */
export function isCricketTieSelectionName(name) {
  return String(name ?? "").trim().toUpperCase() === "TIE";
}

/** Decimal 3-way Match Winner third runner: "Tie" or "Draw" (same UI role). */
export function isCricketTieOrDrawSelectionName(name) {
  return isCricketTieSelectionName(name) || isCricketDrawSelectionName(name);
}

export function findCricketDrawSelection(market) {
  const selections = market?.selectionslist ?? market?.selections;
  if (!Array.isArray(selections)) return null;
  return selections.find((selection) => isCricketDrawSelectionName(selection?.name)) ?? null;
}

export function findCricketTieSelection(market) {
  const selections = market?.selectionslist ?? market?.selections;
  if (!Array.isArray(selections)) return null;
  return selections.find((selection) => isCricketTieSelectionName(selection?.name)) ?? null;
}

/**
 * Third Match Winner runner when offered: Draw on Test, Tie on limited overs.
 * @param {object|null|undefined} market
 * @param {boolean} isTest
 */
export function findCricketMatchWinnerThirdSelection(market, isTest) {
  return isTest ? findCricketDrawSelection(market) : findCricketTieSelection(market);
}

/** UI label for the third Match Winner row on lobby / match cards. */
export function cricketMatchWinnerThirdRowLabel(isTest) {
  return isTest ? "DRAW" : "TIE";
}

export function hasCricketThreeWayMatchWinnerMarket(markets) {
  const market = findCricketThreeWayMatchWinnerMarket(markets);
  if (market == null) return false;
  const selections = market?.selectionslist ?? market?.selections;
  if (!Array.isArray(selections)) return false;
  return selections.some((selection) => isCricketDrawSelectionName(selection?.name));
}

function competitionNameFromLineup(lineup) {
  if (!lineup || typeof lineup !== "object") return null;
  const comp = lineup.competition;
  if (typeof comp === "string") return comp;
  if (comp && typeof comp === "object") {
    return comp.name ?? comp.title ?? comp.competition ?? null;
  }
  return null;
}

/**
 * Infer The Hundred from competition / fixture title when Decimal `format` is missing.
 * @param {unknown} competition
 * @param {unknown} [matchName]
 * @returns {boolean}
 */
export function isCricketHundredMatchFromText(competition, matchName) {
  const blob = `${String(competition ?? "")} ${String(matchName ?? "")}`.toLowerCase();
  if (!blob) return false;
  return /\bthe\s*hundred\b/.test(blob);
}

/**
 * True when a cricket fixture is The Hundred (format 100 or competition/title text).
 * @param {object} [ctx]
 * @returns {boolean}
 */
export function isCricketHundredMatch(ctx = {}) {
  const formatCandidates = [
    ctx.format,
    ctx.eventFormat,
    ctx.matchFormat,
    ctx.match?.format,
    ctx.match?.Format,
    ctx.streamData?.match_update?.format,
    ctx.streamData?.event?.format,
  ];
  for (const raw of formatCandidates) {
    if (raw == null || raw === "") continue;
    if (normalizeCricketFormatCode(raw) === CRICKET_HUNDRED_FORMAT_CODE) return true;
    if (/\bthe\s*hundred\b/.test(String(raw).toLowerCase())) return true;
  }

  const competition =
    ctx.competition ??
    ctx.match?.competition ??
    ctx.streamData?.event?.competition ??
    competitionNameFromLineup(ctx.lineup);
  const matchName = ctx.matchName ?? ctx.match?.name;
  return isCricketHundredMatchFromText(competition, matchName);
}

/**
 * Resolve whether a cricket fixture is Test cricket.
 *
 * Signals (first match wins):
 * - format `999` / `"999"` / `"Test Match"` / similar text
 * - competition or match title text (`Youth Tests`, `1st Test`, `Test Series`, `First Class`, WTC)
 * - `max_overs === -1` or mOvers containing 999 / -1
 * - live feed has Match Winner 3-way (market_type_id 15) with a Draw selection (not Tie)
 *
 * @param {object} [ctx]
 * @returns {boolean}
 */
export function isCricketTestMatch(ctx = {}) {
  const formatCandidates = [
    ctx.format,
    ctx.eventFormat,
    ctx.matchFormat,
    ctx.match?.format,
    ctx.match?.Format,
    ctx.streamData?.match_update?.format,
    ctx.streamData?.event?.format,
  ];
  for (const raw of formatCandidates) {
    if (raw == null || raw === "") continue;
    if (normalizeCricketFormatCode(raw) === CRICKET_TEST_FORMAT_CODE) return true;
    if (isCricketTestFormatRaw(raw)) return true;
  }

  if (isCricketTestMaxOvers(ctx.maxOvers ?? ctx.event?.max_overs ?? ctx.streamData?.event?.max_overs)) {
    return true;
  }
  if (isCricketTestMOvers(ctx.mOvers)) return true;

  const competition =
    ctx.competition ??
    ctx.match?.competition ??
    ctx.streamData?.event?.competition ??
    competitionNameFromLineup(ctx.lineup);
  const matchName = ctx.matchName ?? ctx.match?.name;
  if (isCricketTestMatchFromText(competition, matchName)) return true;

  return hasCricketThreeWayMatchWinnerMarket(ctx.markets ?? ctx.streamData?.market_update?.markets);
}

/**
 * Best-effort numeric format for UI (999 = Test, 20/50 = limited overs).
 * @param {object} [ctx]
 * @returns {number|null}
 */
export function resolveCricketFormatCode(ctx = {}) {
  if (isCricketTestMatch(ctx)) return CRICKET_TEST_FORMAT_CODE;

  const formatCandidates = [
    ctx.format,
    ctx.eventFormat,
    ctx.matchFormat,
    ctx.match?.format,
    ctx.streamData?.match_update?.format,
    ctx.streamData?.event?.format,
  ];
  for (const raw of formatCandidates) {
    const code = normalizeCricketFormatCode(raw);
    if (code != null) return code;
  }
  return null;
}
